import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { GET_CONFIG } from '../gql/queries/appConfig';

/**
 * Key de config por tenant (catálogo FeatureFlag + override AppConfig.settings,
 * patrón `map_default_center`): equipo de vigilancia POR DEFECTO del tenant.
 *
 * - Con valor (teamId): al crear un Recorregut de Vigilància el equipo viene
 *   preseleccionado y el selector de equipo NO se muestra (caso UTE → ÈXSER).
 * - Sin valor / flag inactiva: el wizard muestra el selector de equipo
 *   (elegible entre los equipos `isSurveillance` del tenant).
 *
 * Valor aceptado: el teamId plano ("6869...") o JSON {"teamId":"6869..."}.
 */
export const SURVEILLANCE_DEFAULT_TEAM_KEY = 'surveillance_default_team';

const OBJECT_ID_RE = /^[0-9a-fA-F]{24}$/;

/**
 * Parsea el valor crudo de la config. Devuelve el teamId o null si no valida
 * — el caller decide qué hacer (mostrar selector). Exportada pura para tests.
 */
export function parseDefaultSurveillanceTeamConfig(
  value: string | null | undefined
): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (OBJECT_ID_RE.test(trimmed)) return trimmed;
  try {
    const parsed = JSON.parse(trimmed);
    const teamId = parsed?.teamId;
    if (typeof teamId === 'string' && OBJECT_ID_RE.test(teamId)) return teamId;
  } catch {
    /* JSON roto → misconfiguración: cae al warn de abajo */
  }
  console.warn(
    '[surveillance_default_team] Valor de config inválido, se muestra selector:',
    value
  );
  return null;
}

/**
 * Equipo de vigilancia por defecto del tenant actual (JWT), o null si el
 * tenant no lo tiene configurado. NO usa useFeatureFlag (coerciona a boolean);
 * lee GET_CONFIG crudo. Deriva con useMemo directo sobre data (lección BUG
 * #20: nada de useState+useEffect). Mismo patrón que useTenantMapCenter.
 */
export function useTenantDefaultSurveillanceTeam(): string | null {
  const { data } = useQuery(GET_CONFIG, {
    variables: { key: SURVEILLANCE_DEFAULT_TEAM_KEY },
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore',
  });

  return useMemo(
    () => parseDefaultSurveillanceTeamConfig(data?.getConfig),
    [data?.getConfig]
  );
}
